import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  Autocomplete,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableContainer,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  LinearProgress,
  Alert,
  Tooltip,
  IconButton,
  FormHelperText,
} from "@mui/material";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { StyledTextField, StyledSelect } from "../../customComponents/styled/styledInputs";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { useGetClassesPaginatedQuery } from "../classroom/classApiSlice";
import { useGetSessionsPaginatedQuery } from "../settings/session/sessionApiSlice";
import {
  useGetSubjectReconciliationQuery,
  useReconcileSubjectRecordsMutation,
} from "./subjectReconciliationApiSlice";
import toast from "../../../helpers/toast";

// A stable color per subject id, so the same subject can be visually matched
// across the table, suggested-match cards, and both sides of the reconcile
// dialog — this is the main way to tell apart two subjects whose names look
// identical (e.g. differ only by trailing whitespace or casing).
const SUBJECT_COLORS = [
  "#e53935", "#8e24aa", "#3949ab", "#1e88e5", "#00897b",
  "#43a047", "#fb8c00", "#6d4c41", "#546e7a", "#d81b60", "#00acc1",
];
function getSubjectColor(id) {
  const n = Number(id) || 0;
  return SUBJECT_COLORS[n % SUBJECT_COLORS.length];
}

function SubjectColorDot({ id, size = 10 }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: getSubjectColor(id),
        flexShrink: 0,
      }}
    />
  );
}

// Renders the name in a bordered, monospaced box — so a trailing/leading
// space or a casing difference between two otherwise-identical-looking
// subjects becomes visible instead of invisible plain text.
function SubjectLabel({ subject, showCount = false }) {
  if (!subject) return null;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, maxWidth: "100%" }}>
      <SubjectColorDot id={subject.id} />
      <Box
        component="span"
        sx={{
          fontFamily: "monospace",
          fontSize: "0.85em",
          bgcolor: "action.hover",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 0.5,
          px: 0.6,
          py: 0.1,
          whiteSpace: "pre",
        }}
      >
        {subject.name}
      </Box>
      {showCount && (
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
          ({subject.total_records ?? 0} records)
        </Typography>
      )}
    </Box>
  );
}

function TermCoverageRow({ terms, subject }) {
  return (
    <Box>
      <Box sx={{ mb: 0.5 }}>
        <SubjectLabel subject={subject} />
      </Box>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {terms.map((term) => {
          const count = subject.term_counts?.[term.id];
          return (
            <Tooltip key={term.id} title={`${term.name}: ${count || 0} record(s)`}>
              <Chip
                label={term.name}
                size="small"
                color={count ? "success" : "default"}
                variant={count ? "filled" : "outlined"}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

function ReconcileDialog({ open, onClose, classroomId, terms, subjects, initialFromSubject, initialToSubject }) {
  const [fromSubject, setFromSubject] = useState(initialFromSubject || null);
  const [toSubject, setToSubject] = useState(initialToSubject || null);
  const [termId, setTermId] = useState("");
  const [reconcile, { isLoading }] = useReconcileSubjectRecordsMutation();

  const availableTerms = terms.filter((t) => fromSubject?.covered_term_ids?.includes(t.id));

  useEffect(() => {
    setTermId(availableTerms.length === 1 ? availableTerms[0].id : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromSubject?.id]);

  const handleSwap = () => {
    setFromSubject(toSubject);
    setToSubject(fromSubject);
  };

  const handleSubmit = async () => {
    if (!fromSubject || !toSubject || !termId) return;
    try {
      const result = await reconcile({
        classroom_id: classroomId,
        term_id: termId,
        from_subject_id: fromSubject.id,
        to_subject_id: toSubject.id,
      }).unwrap();
      const parts = [`${result.records_moved} record(s)`];
      if (result.comments_moved) parts.push(`${result.comments_moved} comment(s)`);
      let message = `Moved ${parts.join(" and ")} to "${toSubject.name}".`;
      const conflicted = (result.records_conflicted || 0) + (result.comments_conflicted || 0);
      if (conflicted) {
        message += ` ${conflicted} entr${conflicted === 1 ? "y" : "ies"} already existed under the target subject and were left alone — check those manually.`;
      }
      toast.success(message);
      onClose();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to reconcile records");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        Reconcile Subject Records
        <IconButton size="small" onClick={onClose}>
          <CloseSharpIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This moves records and comments for the chosen term from one subject onto the other, in this
          class only. Both subjects stay independent everywhere else — nothing is renamed or deleted.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <Typography variant="formSubHeading">FROM (mistaken subject)</Typography>
            <Autocomplete
              options={subjects.filter((s) => s.id !== toSubject?.id)}
              getOptionLabel={(o) => o.name || ""}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={fromSubject}
              onChange={(_, value) => setFromSubject(value)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <SubjectLabel subject={option} showCount />
                </li>
              )}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  placeholder="Subject"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: fromSubject ? (
                      <Box sx={{ display: "flex", ml: 0.5 }}>
                        <SubjectColorDot id={fromSubject.id} />
                      </Box>
                    ) : null,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={2} sx={{ display: "flex", justifyContent: "center" }}>
            <Tooltip title="Swap">
              <span>
                <IconButton onClick={handleSwap} disabled={!fromSubject || !toSubject}>
                  <SwapHorizIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={5}>
            <Typography variant="formSubHeading">TO (correct subject)</Typography>
            <Autocomplete
              options={subjects.filter((s) => s.id !== fromSubject?.id)}
              getOptionLabel={(o) => o.name || ""}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={toSubject}
              onChange={(_, value) => setToSubject(value)}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <SubjectLabel subject={option} showCount />
                </li>
              )}
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  placeholder="Subject"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: toSubject ? (
                      <Box sx={{ display: "flex", ml: 0.5 }}>
                        <SubjectColorDot id={toSubject.id} />
                      </Box>
                    ) : null,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="formSubHeading">TERM</Typography>
            <StyledSelect
              fullWidth
              displayEmpty
              value={termId}
              onChange={(e) => setTermId(e.target.value)}
              disabled={!fromSubject}
            >
              <MenuItem value="" disabled>
                <em>Select a term</em>
              </MenuItem>
              {availableTerms.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </StyledSelect>
            {fromSubject && availableTerms.length === 0 && (
              <FormHelperText>"{fromSubject.name}" has no records in this class/session.</FormHelperText>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <StyledLoadingButton variant="outlined" onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </StyledLoadingButton>
        <StyledLoadingButton
          loading={isLoading}
          disabled={!fromSubject || !toSubject || !termId}
          sx={{ textTransform: "none" }}
          onClick={handleSubmit}
        >
          Reconcile Records
        </StyledLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function SubjectReconciliation() {
  const [classroomSearch, setClassroomSearch] = useState("");
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 10, q: classroomSearch });
  const classrooms = classroomsData?.classrooms ?? [];

  const [sessionSearch, setSessionSearch] = useState("");
  const { data: sessionsData } = useGetSessionsPaginatedQuery({ page: 1, per_page: 5, q: sessionSearch });
  const sessions = sessionsData?.sessions ?? [];

  const [classroom, setClassroom] = useState(null);
  const [session, setSession] = useState(null);
  const [dialogState, setDialogState] = useState(null);

  const { data, isFetching, isError } = useGetSubjectReconciliationQuery(
    { classroom_id: classroom?.id, session_id: session?.id },
    { skip: !classroom?.id || !session?.id }
  );

  const terms = data?.terms ?? [];
  const subjects = data?.subjects ?? [];
  const suggestedPairs = data?.suggested_pairs ?? [];

  const openDialogFor = (fromSubject, toSubject = null) => setDialogState({ fromSubject, toSubject });

  return (
    <Box sx={{ px: { xs: 0, sm: 1 } }}>
      <StyledPaper sx={{ padding: "15px", mb: 2 }}>
        <Typography variant="formHeading" sx={{ mb: 1 }}>
          Reconcile Records
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          If the same subject was entered under two different names in different terms — e.g. "English" in
          Term 1 and "English Language" in Term 2 — cumulative reports and broadsheets can't combine them,
          since they're technically different subjects. Pick a class and session below to check for gaps,
          then point the wrong term's records at the correct subject.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="formSubHeading">CLASS</Typography>
            <Autocomplete
              options={classrooms}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={classroom}
              onChange={(_, value) => setClassroom(value)}
              onInputChange={(_, value, reason) => { if (reason === "input") setClassroomSearch(value); }}
              filterOptions={(x) => x}
              renderInput={(params) => <StyledTextField {...params} placeholder="Search class..." />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="formSubHeading">SESSION</Typography>
            <Autocomplete
              options={sessions}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={session}
              onChange={(_, value) => setSession(value)}
              onInputChange={(_, value, reason) => { if (reason === "input") setSessionSearch(value); }}
              filterOptions={(x) => x}
              renderInput={(params) => <StyledTextField {...params} placeholder="Search session..." />}
            />
          </Grid>
        </Grid>
      </StyledPaper>

      {isFetching && <LinearProgress sx={{ mb: 2 }} />}

      {classroom && session && !isFetching && (
        <>
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load subject data for this class/session.
            </Alert>
          )}

          {!isError && terms.length === 0 && (
            <Alert severity="info">This session has no terms yet.</Alert>
          )}

          {!isError && terms.length > 0 && subjects.length === 0 && (
            <Alert severity="info">No subject records found for this class in this session yet.</Alert>
          )}

          {suggestedPairs.length > 0 && (
            <StyledPaper sx={{ padding: "15px", mb: 2 }}>
              <Typography variant="formHeading" sx={{ mb: 1.5 }}>
                Suggested Matches
              </Typography>
              <Grid container spacing={2}>
                {suggestedPairs.map((pair, idx) => (
                  <Grid item xs={12} md={6} key={idx}>
                    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
                        <SubjectLabel subject={pair.subject_a} showCount />
                        <SwapHorizIcon fontSize="small" color="action" />
                        <SubjectLabel subject={pair.subject_b} showCount />
                      </Box>
                      <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
                        <TermCoverageRow terms={terms} subject={pair.subject_a} />
                        <TermCoverageRow terms={terms} subject={pair.subject_b} />
                      </Box>
                      <StyledLoadingButton
                        size="small"
                        sx={{ textTransform: "none" }}
                        onClick={() => openDialogFor(pair.subject_a, pair.subject_b)}
                      >
                        Reconcile
                      </StyledLoadingButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </StyledPaper>
          )}

          {subjects.length > 0 && (
            <StyledPaper sx={{ paddingBottom: "10px" }}>
              <Box sx={{ padding: "15px" }}>
                <Typography variant="formHeading">All Subjects This Session</Typography>
              </Box>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ borderBottom: "1.5px solid lightgrey" }}>
                      <StyledTableCell>SUBJECT</StyledTableCell>
                      <StyledTableCell>RECORDS</StyledTableCell>
                      {terms.map((term) => (
                        <StyledTableCell key={term.id}>{term.name}</StyledTableCell>
                      ))}
                      <StyledTableCell>ACTIONS</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subjects.map((subject) => (
                      <StyledTableRow key={subject.id}>
                        <StyledTableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <SubjectLabel subject={subject} />
                            {subject.incomplete && (
                              <Chip label="Incomplete" size="small" color="warning" variant="outlined" />
                            )}
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>{subject.total_records}</StyledTableCell>
                        {terms.map((term) => {
                          const count = subject.term_counts?.[term.id];
                          return (
                            <StyledTableCell key={term.id}>
                              {count ? (
                                <Tooltip title={`${count} record(s)`}>
                                  <CheckCircleIcon fontSize="small" color="success" />
                                </Tooltip>
                              ) : (
                                <Tooltip title="No records">
                                  <CancelIcon fontSize="small" color="disabled" />
                                </Tooltip>
                              )}
                            </StyledTableCell>
                          );
                        })}
                        <StyledTableCell>
                          <StyledLoadingButton
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: "none" }}
                            onClick={() => openDialogFor(subject)}
                          >
                            Reconcile…
                          </StyledLoadingButton>
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </StyledPaper>
          )}
        </>
      )}

      {dialogState && (
        <ReconcileDialog
          open={Boolean(dialogState)}
          onClose={() => setDialogState(null)}
          classroomId={classroom?.id}
          terms={terms}
          subjects={subjects}
          initialFromSubject={dialogState.fromSubject}
          initialToSubject={dialogState.toSubject}
        />
      )}
    </Box>
  );
}

export default SubjectReconciliation;
